import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { industries, platformModifiers, Platform } from './attributionData';
const fmt = (val: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
}).format(val);
const fmtNum = (val: number) => new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0
}).format(val);
const AnimatedNumber = ({
  value,
  format = 'number'
}: {
  value: number;
  format?: 'number' | 'currency' | 'ratio';
}) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const dur = 350;
    const frames = 16;
    const step = (value - display) / frames;
    let cur = display;
    let f = 0;
    const t = setInterval(() => {
      f++;
      cur += step;
      if (f >= frames) {
        clearInterval(t);
        setDisplay(value);
      } else setDisplay(cur);
    }, dur / frames);
    return () => clearInterval(t);
  }, [value]);
  if (format === 'currency') return <span data-id="element-643">{fmt(display)}</span>;
  if (format === 'ratio') return <span data-id="element-644">{display.toFixed(1)}:1</span>;
  return <span data-id="element-645">{fmtNum(display)}</span>;
};
export function TrafficSourcesSlide() {
  const [industryKey, setIndustryKey] = useState('medspa');
  const industry = industries[industryKey];
  const [totalSpend, setTotalSpend] = useState(30000);
  const [toggleValue, setToggleValue] = useState(industry.toggle.default);
  const [includeReferrals, setIncludeReferrals] = useState(false);
  useEffect(() => {
    setToggleValue(industries[industryKey].toggle.default);
  }, [industryKey]);
  const m = useMemo(() => {
    let leads = 0,
      appts = 0,
      shows = 0,
      purchases = 0;
    (Object.keys(platformModifiers) as Platform[]).forEach(p => {
      const spend = totalSpend * (industry.distribution[p] / 100);
      const cpl = industry.cpl * platformModifiers[p].cplMult;
      const pl = spend / cpl;
      const pa = pl * industry.leadToAppt;
      const ps = pa * industry.apptToShow;
      const pp = ps * (industry.showToPurchase * platformModifiers[p].closeMult);
      leads += pl;
      appts += pa;
      shows += ps;
      purchases += pp;
    });
    const {
      ltvMultiplier = 1,
      ticketMultiplier = 1,
      marginBoost = 0,
      conversionMultiplier = 1
    } = industry.toggle.effect(toggleValue);
    // Apply conversion multiplier to funnel stages
    appts *= conversionMultiplier;
    shows *= conversionMultiplier;
    purchases *= conversionMultiplier;
    if (includeReferrals) purchases *= industry.referralMultiplier;
    const ticket = industry.firstVisitTicket * ticketMultiplier;
    const revFirst = Math.round(purchases * ticket / 100) * 100;
    const revGoBack = Math.round(purchases * industry.goBack12mo * ltvMultiplier / 100) * 100;
    const revLTV = Math.round(purchases * industry.ltv5yr * ltvMultiplier / 100) * 100;
    const margin = industry.margin + marginBoost;
    const profit = Math.round(revLTV * margin / 100) * 100;
    const cac = totalSpend / (purchases || 1);
    return {
      leads: Math.round(leads),
      appts: Math.round(appts),
      shows: Math.round(shows),
      purchases: Math.round(purchases),
      revFirst,
      revGoBack,
      revLTV,
      profit,
      cpl: totalSpend / (leads || 1),
      cpa: totalSpend / (appts || 1),
      cps: totalSpend / (shows || 1),
      cac,
      roasLTV: revLTV / totalSpend,
      ltvToCac: industry.ltv5yr / cac
    };
  }, [industry, totalSpend, toggleValue, includeReferrals]);
  const cacColor = m.ltvToCac < 3 ? 'text-red-500' : m.ltvToCac < 10 ? 'text-[#D4A944]' : m.ltvToCac < 20 ? 'text-[#8FBC8F]' : 'text-[#C9A961]';
  const funnel = [{
    label: 'Leads',
    val: m.leads,
    color: '#555'
  }, {
    label: 'Appts',
    val: m.appts,
    color: '#777'
  }, {
    label: 'Shows',
    val: m.shows,
    color: '#999'
  }, {
    label: 'Purchases',
    val: m.purchases,
    color: '#C9A961'
  }];
  return <section id="traffic-sources" className="bg-[#0A0A0A] text-[#F5F1EA] py-12 px-4 md:px-6 overflow-x-hidden" data-id="element-646">
      <div className="max-w-7xl mx-auto" data-id="element-647">
        {/* Row 1: Title + Industry + Spend */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-6" data-id="element-648">
          <div className="flex-shrink-0" data-id="element-649">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-650">
              Attribution Calculator
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-651">
              See what your ad spend should produce.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-4 flex-1" data-id="element-652">
            {/* Industry */}
            <div className="relative" data-id="element-653">
              <select value={industryKey} onChange={e => setIndustryKey(e.target.value)} className="appearance-none bg-[#1A1A1A] border border-[#333] rounded-lg py-2 pl-3 pr-8 text-sm text-[#F5F1EA] focus:outline-none focus:border-[#C9A961]" data-id="element-654">
                {Object.entries(industries).map(([k, v]) => <option key={k} value={k} data-id="element-655">
                    {v.name}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-656" />
            </div>
            {/* Spend slider */}
            <div className="flex-1 min-w-[200px]" data-id="element-657">
              <div className="flex justify-between text-xs text-gray-400 mb-1" data-id="element-658">
                <span data-id="element-659">Monthly Ad Spend</span>
                <span className="text-[#C9A961] font-bold text-sm" data-id="element-660">
                  {fmt(totalSpend)}
                </span>
              </div>
              <input type="range" min="1000" max="100000" step="1000" value={totalSpend} onChange={e => setTotalSpend(Number(e.target.value))} className="w-full h-1.5 bg-[#222] rounded-lg appearance-none cursor-pointer accent-[#C9A961]" data-id="element-661" />
            </div>
            {/* Growth Lever inline */}
            <div className="flex items-center gap-2 bg-[#111] border border-[#C9A961]/30 rounded-lg px-3 py-2" data-id="element-662">
              <span className="bg-[#C9A961] text-black text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" data-id="element-663">
                <TrendingUp className="w-2.5 h-2.5" data-id="element-664" /> Lever
              </span>
              {industry.toggle.isToggle ? <label className="flex items-center cursor-pointer gap-2" data-id="element-665">
                  <span className="text-xs text-gray-300 whitespace-nowrap" title={industry.toggle.description} data-id="element-666">
                    {industry.toggle.label}
                  </span>
                  <div className="relative" data-id="element-667">
                    <input type="checkbox" className="sr-only" checked={toggleValue === 1} onChange={e => setToggleValue(e.target.checked ? 1 : 0)} data-id="element-668" />
                    <div className={`w-9 h-5 rounded-full transition-colors ${toggleValue === 1 ? 'bg-[#C9A961]' : 'bg-[#333]'}`} data-id="element-669" />
                    <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${toggleValue === 1 ? 'translate-x-4' : ''}`} data-id="element-670" />
                  </div>
                </label> : <div className="flex items-center gap-2" data-id="element-671">
                  <span className="text-xs text-gray-300 whitespace-nowrap cursor-help" title={industry.toggle.description} data-id="element-672">
                    {industry.toggle.label}
                  </span>
                  <input type="range" min="0" max={industry.toggle.stretch} step="1" value={toggleValue} onChange={e => setToggleValue(Number(e.target.value))} className="w-20 h-1 bg-[#222] rounded appearance-none cursor-pointer accent-[#C9A961]" data-id="element-673" />
                  <span className="text-xs font-bold text-white w-8" data-id="element-674">
                    {toggleValue}%
                  </span>
                </div>}
            </div>
            {/* Referral toggle */}
            <label className="flex items-center gap-2 cursor-pointer bg-[#111] border border-[#1E1E1E] rounded-lg px-3 py-2" data-id="element-675">
              <span className="text-xs text-gray-400 whitespace-nowrap" data-id="element-676">
                Referrals
              </span>
              <div className="relative" data-id="element-677">
                <input type="checkbox" className="sr-only" checked={includeReferrals} onChange={e => setIncludeReferrals(e.target.checked)} data-id="element-678" />
                <div className={`w-8 h-4.5 rounded-full transition-colors ${includeReferrals ? 'bg-[#C9A961]' : 'bg-[#333]'}`} style={{
                width: 32,
                height: 18
              }} data-id="element-679" />
                <div className={`absolute left-0.5 top-0.5 bg-white w-3.5 h-3.5 rounded-full transition-transform`} style={{
                width: 14,
                height: 14,
                transform: includeReferrals ? 'translateX(14px)' : 'translateX(0)'
              }} data-id="element-680" />
              </div>
            </label>
          </div>
        </div>

        {/* Revenue row - consolidated inline */}
        <div className="flex flex-col md:flex-row items-stretch gap-3 mb-4 bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-id="element-681">
          <div className="flex-1 text-center md:text-left" data-id="element-682">
            <p className="text-[10px] uppercase tracking-widest text-gray-500" data-id="element-683">
              First Visit
            </p>
            <div className="text-xl font-bold text-gray-400" data-id="element-684">
              <AnimatedNumber value={m.revFirst} format="currency" data-id="element-685" />
            </div>
          </div>
          <div className="hidden md:block w-px bg-[#2A2A2A]" data-id="element-686" />
          <div className="flex-1 text-center md:text-left" data-id="element-687">
            <p className="text-[10px] uppercase tracking-widest text-gray-400" data-id="element-688">
              12-Mo Go-Back
            </p>
            <div className="text-xl font-bold text-[#F5F1EA]" data-id="element-689">
              <AnimatedNumber value={m.revGoBack} format="currency" data-id="element-690" />
            </div>
          </div>
          <div className="hidden md:block w-px bg-[#2A2A2A]" data-id="element-691" />
          <div className="flex-1 text-center md:text-left" data-id="element-692">
            <p className="text-[10px] uppercase tracking-widest text-[#C9A961]" data-id="element-693">
              5-Year LTV
            </p>
            <div className="text-2xl font-bold text-[#C9A961]" data-id="element-694">
              <AnimatedNumber value={m.revLTV} format="currency" data-id="element-695" />
            </div>
          </div>
        </div>

        {/* Row: Funnel + Metrics side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" data-id="element-696">
          {/* Compact Funnel */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-id="element-697">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-698">
              Sales Funnel
            </p>
            <div className="space-y-1.5" data-id="element-699">
              {funnel.map((s, i) => <div key={s.label} className="flex items-center gap-2" data-id="element-700">
                  <span className="text-[11px] text-gray-400 w-16 shrink-0" data-id="element-701">
                    {s.label}
                  </span>
                  <div className="flex-1 bg-[#1A1A1A] h-2 rounded overflow-hidden" data-id="element-702">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.max(3, s.val / (m.leads || 1) * 100)}%`
                }} transition={{
                  duration: 0.6,
                  ease: 'easeOut'
                }} className="h-full rounded" style={{
                  backgroundColor: s.color
                }} data-id="element-703" />
                  </div>
                  <span className="text-[11px] font-bold text-white w-10 text-right" data-id="element-704">
                    <AnimatedNumber value={s.val} data-id="element-705" />
                  </span>
                </div>)}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-2" data-id="element-706">
            {[{
            label: 'CPL',
            val: m.cpl,
            fmt: 'currency' as const
          }, {
            label: 'Cost/Appt',
            val: m.cpa,
            fmt: 'currency' as const
          }, {
            label: 'CAC',
            val: m.cac,
            fmt: 'currency' as const
          }, {
            label: 'ROAS (LTV)',
            val: m.roasLTV,
            fmt: 'number' as const,
            suffix: 'x'
          }, {
            label: 'Cost/Show',
            val: m.cps,
            fmt: 'currency' as const
          }, {
            label: 'LTV:CAC',
            val: m.ltvToCac,
            fmt: 'ratio' as const,
            highlight: true
          }].map(card => <div key={card.label} className={`rounded-lg p-3 relative group ${card.highlight ? 'bg-[#1A1A1A] border border-[#333] cursor-help' : 'bg-[#111] border border-[#1E1E1E]'}`} data-id="element-707">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-708">
                  {card.label}
                </p>
                <div className={`text-lg font-bold ${card.highlight ? cacColor : 'text-white'}`} data-id="element-709">
                  {card.fmt === 'number' ? <>
                      <AnimatedNumber value={card.val} data-id="element-710" />
                      {card.suffix}
                    </> : <AnimatedNumber value={card.val} format={card.fmt} data-id="element-711" />}
                </div>
                {card.highlight && <div className="absolute bottom-full right-0 mb-2 w-72 bg-[#1A1A1A] border border-[#333] rounded-xl p-4 shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 z-50" data-id="element-712">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A961] mb-3" data-id="element-713">
                      LTV:CAC Cheat Sheet
                    </p>
                    <div className="space-y-2 text-[11px]" data-id="element-714">
                      <div className="flex gap-2" data-id="element-715">
                        <span className="text-red-500 font-bold shrink-0 w-14" data-id="element-716">
                          Below 1:1
                        </span>
                        <span className="text-gray-400" data-id="element-717">
                          You're losing money on every client. Stop spending
                          immediately.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-718">
                        <span className="text-red-400 font-bold shrink-0 w-14" data-id="element-719">
                          1:1 - 3:1
                        </span>
                        <span className="text-gray-400" data-id="element-720">
                          Surviving but not building wealth. Most "profit" goes
                          back into ads.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-721">
                        <span className="text-[#D4A944] font-bold shrink-0 w-14" data-id="element-722">
                          3:1
                        </span>
                        <span className="text-gray-400" data-id="element-723">
                          Industry-accepted healthy benchmark. You're
                          sustainable.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-724">
                        <span className="text-[#8FBC8F] font-bold shrink-0 w-14" data-id="element-725">
                          3:1 - 10:1
                        </span>
                        <span className="text-gray-400" data-id="element-726">
                          You're profitable. Marketing is working.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-727">
                        <span className="text-[#8FBC8F] font-bold shrink-0 w-14" data-id="element-728">
                          Above 10:1
                        </span>
                        <span className="text-gray-400" data-id="element-729">
                          You should be spending MORE on ads. You're
                          under-investing.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-730">
                        <span className="text-[#C9A961] font-bold shrink-0 w-14" data-id="element-731">
                          20:1+
                        </span>
                        <span className="text-gray-400" data-id="element-732">
                          Exceptional retention or under-spending on growth.
                          Probably both.
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Annual Projection Strip */}
        <div className="bg-gradient-to-r from-[#1A150A] via-[#1E1A0E] to-[#1A150A] border border-[#C9A961]/20 rounded-lg px-4 py-3 text-center" data-id="element-733">
          <p className="text-xs md:text-sm text-[#F5F1EA]" data-id="element-734">
            Over 12 months:{' '}
            <strong className="text-[#C9A961]" data-id="element-735">
              <AnimatedNumber value={m.revGoBack * 12} format="currency" data-id="element-736" />
            </strong>{' '}
            go-back revenue |{' '}
            <strong className="text-[#8FBC8F]" data-id="element-737">
              <AnimatedNumber value={m.profit} format="currency" data-id="element-738" />
            </strong>{' '}
            5yr profit |{' '}
            <strong className="text-white" data-id="element-739">
              <AnimatedNumber value={m.purchases * 12} data-id="element-740" />
            </strong>{' '}
            new clients
          </p>
        </div>

        {/* Footer */}
        <p className="text-[10px] text-gray-600 text-center mt-4 max-w-3xl mx-auto" data-id="element-741">
          Projections based on industry benchmarks from AmSpa, ServiceTitan,
          FieldRoutes, Profitability Partners, FieldEdge, WhatConverts, and 200+
          P&L reviews. Actual results depend on creative quality, offer quality,
          and local market.
        </p>
      </div>
    </section>;
}